import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { ChampionshipUserLineups } from '#src/components/championship/ChampionshipUserLineups/index.tsx';

import type {
  ChampionshipPage_so5UserGroup,
  ChampionshipPage_user,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  max-height: calc(100vh - var(--current-stack-height));
  overflow: auto;
  background-size: 100%;
  background-repeat: no-repeat;
  background-image: linear-gradient(
    180deg,
    var(--color) 0%,
    var(--current-background-color) 100%
  );
`;

type Props = {
  so5UserGroup: ChampionshipPage_so5UserGroup;
  user: ChampionshipPage_user;
};
export const ChampionshipPage = ({ so5UserGroup, user }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const { track } = useEventsContext();
  const isCurrentUser = user.slug === currentUser?.slug;

  const [cachedGroupMetadata, setCachedGroupMetadata] =
    useState<typeof so5UserGroup>();

  if (so5UserGroup?.slug && so5UserGroup?.slug !== cachedGroupMetadata?.slug) {
    setCachedGroupMetadata({ ...so5UserGroup });
  }

  useEffect(() => {
    if (so5UserGroup.slug) {
      track(`View Chase Championship Tab`, {
        of_current_user: isCurrentUser,
        user_group_slug: so5UserGroup.slug,
        sport,
      });
    }
  }, [isCurrentUser, so5UserGroup.slug, sport, track]);

  return (
    <Root>
      <Vertical>
        {!!so5UserGroup && !!user && (
          <ChampionshipUserLineups user={user} so5UserGroup={so5UserGroup} />
        )}
      </Vertical>
    </Root>
  );
};

ChampionshipPage.fragments = {
  so5UserGroup: gql`
    fragment ChampionshipPage_so5UserGroup on So5UserGroup {
      slug
      ...ChampionshipUserLineups_so5UserGroup
    }
    ${ChampionshipUserLineups.fragments.so5UserGroup}
  ` as TypedDocumentNode<ChampionshipPage_so5UserGroup>,
  user: gql`
    fragment ChampionshipPage_user on User {
      slug
      ...ChampionshipUserLineups_user
    }
    ${ChampionshipUserLineups.fragments.user}
  ` as TypedDocumentNode<ChampionshipPage_user>,
};
